import { FormProvider, useForm } from "react-hook-form";
import PracticeTitleContent from "../../../components/PracticeTitleContent";
import SelectableGroup from "../../../components/formElements/SelectableGroup";
import TitleEditor from "./TitleEditor";
import { useEffect, useState } from "react";
import { myAxios, sanitizeUserInput } from "../../../utils/common";
import clsx from "clsx";
import { useParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { qoGetSinglePractice } from "../../../utils/queryOptions";
import { IPracticeTest, TPracticeTypes } from "../../../types/practiceTypes";
import { toast } from "react-toastify";
import { TPracticeLang } from "../adminTypes";

type TTestPracticeForm = {
  answer: string[];
};

interface IAddTestForm {
  unit_ml_part_id: number;
  type: TPracticeTypes;
  description: {
    title: string;
    options: string[];
    answer: string;
  };
  language: TPracticeLang;
}

export default function AddTestPractice({ partId }: { partId?: number }) {
  const { practiceId } = useParams();
  const methods = useForm<TTestPracticeForm>();

  const [form, setForm] = useState<{
    title: string;
    answer: number[];
    options: string[];
    language: TPracticeLang;
  }>({
    title: "Deneme 1 2",
    answer: [],
    options: ["Seçim 1", "Seçim 2"],
    language: null,
  });

  const [selectType, setSelectType] = useState<"MULTI" | "SINGLE">("SINGLE");

  const addTestPracticeMutation = useMutation({
    mutationFn: async (data: IAddTestForm) =>
      myAxios.post("/unit-ml-part-practice/", data).then((r) => r.data),
    onSuccess: (stuff) => {
      console.log(stuff);
      toast.success("Soru eklendi!");
    },
  });

  const { data: practice, isSuccess } = useQuery(
    qoGetSinglePractice({
      practiceId: Number(practiceId),
    })
  );

  useEffect(() => {
    if (practiceId && isSuccess && practice.type === "TEST") {
      const { title, answer, options } = practice.description;
      const answerArr = answer.split(",").map((s) => Number(s));

      setForm({
        title,
        answer: answerArr,
        options,
        language: (practice.language || null) as TPracticeLang,
      });

      setSelectType(answerArr.length > 1 ? "MULTI" : "SINGLE");
    }
  }, [practiceId, isSuccess, practice]);

  const onSubmit = (data: TTestPracticeForm) => {
    const newPractice = {
      unit_ml_part_id: partId || 1000,
      type: "TEST" as TPracticeTypes,
      description: {
        title: form.title,
        options: form.options,
        answer:
          typeof data.answer === "string"
            ? data.answer
            : data.answer
              .map((s) => Number(s))
              .sort()
              .join(","),
      },
      language: form.language,
    };
    /* edit ya da post querysi */
    addTestPracticeMutation.mutate(newPractice);
  };

  return (
    <div className="max-w-3xl mx-auto pt-4 space-y-3 w-full">
      <div className="bg-black/60 rounded-2xl border border-white/10">
        <div className="flex border-b border-white/10">
          <div className="flex-1 flex gap-2 items-center p-4">
            <div className="text-lg/none text-black w-7.5 py-1.5 font-bold text-center bg-yellow-600 rounded-full">
              1
            </div>
            <div className="font-medium text-lg mr-auto">Dil?</div>
            <select
              name="language"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  language: e.target.value as TPracticeLang,
                }))
              }
              defaultValue={
                practiceId ? form.language || "javascript" : "python"
              }
              className="bg-white/20 p-2 pr-4 rounded-lg"
            >
              <option value="python">Python</option>
              <option value="java">Java</option>
              <option value="sql">SQL</option>
              <option value="html">HTML</option>
              <option value="css">CSS</option>
              <option value="javascript">Javascript</option>
            </select>
          </div>
          <div className="w-px bg-white/10 self-stretch" />
          <div className="flex-1 flex gap-2 items-center p-4">
            <div className="text-lg/none text-black w-7.5 py-1.5 font-bold text-center bg-yellow-600 rounded-full">
              2
            </div>
            <div className="font-medium text-lg">Cevap sayısı?</div>
            <div className="flex p-1 text-sm ml-auto bg-white/15 font-bold rounded-lg">
              <div
                onClick={() => setSelectType("SINGLE")}
                className={clsx(
                  "py-1.5 px-3 rounded",
                  selectType === "SINGLE" ? "bg-myGreen/50" : "cursor-pointer"
                )}
              >
                Tek
              </div>
              <div
                onClick={() => setSelectType("MULTI")}
                className={clsx(
                  "py-1.5 px-3 rounded",
                  selectType === "MULTI" ? "bg-myGreen/50" : "cursor-pointer"
                )}
              >
                Çok
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-white/10 p-4">
          <TitleEditor
            value={form.title}
            setValue={(newTitle) =>
              setForm((prev) => ({ ...prev, title: newTitle }))
            }
            step={3}
          >
            Soru metnini yaz
          </TitleEditor>
        </div>

        <div className="border-b border-white/10 p-4">
          <TitleEditor
            value={form.options.join("\n")}
            setValue={(newOptions) =>
              setForm((prev) => ({ ...prev, options: newOptions.split("\n") }))
            }
            step={4}
          >
            Her şıkkı yeni bir satıra yaz
          </TitleEditor>
        </div>

        <div className="flex gap-2 p-4">
          <div className="text-lg/none text-black w-7.5 py-1.5 font-bold text-center bg-yellow-600 rounded-full">
            5
          </div>
          <div className="font-medium text-lg">
            Alttaki preview alanıdır. Burada doğru cevapları işaretle. Sonra
            kaydet.
          </div>
        </div>
      </div>

      <div className="bg-black/80 rounded-3xl p-2 border border-myGb/50">
        <div className="flex transition-all relative group rounded-2xl text-white">
          <div className="text-base leading-normal font-medium practiceTitle p-10 grow min-w-72">
            <PracticeTitleContent title={sanitizeUserInput(form.title)} />
          </div>

          <FormProvider {...methods}>
            <div className="p-8 pl-0 grow min-w-72">
              <form
                onSubmit={methods.handleSubmit(onSubmit)}
                id="add_practice_form"
              >
                <SelectableGroup
                  answer=""
                  options={form.options}
                  isRevealed={false}
                  qId={100}
                  defaultValue={
                    practiceId
                      ? (practice as IPracticeTest)?.description.answer
                      : ""
                  }
                  canReveal={false}
                  isDisabled={false}
                  type={selectType}
                />
              </form>
            </div>
          </FormProvider>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <button
          className="practice-button"
          type="submit"
          form="add_practice_form"
        >
          Kaydet
        </button>
        {methods.formState.errors["answer"] && (
          <div className="bg-myRed text-xs py-2 px-3 rounded-full text-white font-semibold self-center">
            {methods.formState.errors["answer"].message as string}
          </div>
        )}
      </div>
    </div>
  );
}
